// src/utils/dateUtils.js
import { format, isAfter, endOfDay, parseISO } from 'date-fns';

/**
 * Format a date string or Date object into a readable format
 * @param {string|Date} date - The date to format
 * @param {string} formatType - The format: 'short', 'medium', 'long', 'full' or a custom format string
 * @returns {string} Formatted date
 */
export const formatDate = (date, formatType = 'medium') => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    // Use built-in Intl.DateTimeFormat for common formats
    switch (formatType) {
      case 'short':
        return new Intl.DateTimeFormat('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }).format(dateObj);
        
      case 'medium':
        return new Intl.DateTimeFormat('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }).format(dateObj);
        
      case 'long':
        return new Intl.DateTimeFormat('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          weekday: 'short' 
        }).format(dateObj);
        
      case 'full':
        return new Intl.DateTimeFormat('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          weekday: 'long',
          hour: '2-digit',
          minute: '2-digit'
        }).format(dateObj);
    
      default:
        // For custom formats, use date-fns format
        if (typeof formatType === 'string' && formatType.includes('yyyy')) {
          return format(dateObj, formatType);
        }
        
        // Default to medium format
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(dateObj);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format a date with time
 * @param {string|Date} date - The date to format
 * @param {boolean} includeSeconds - Whether to include seconds
 * @returns {string} Formatted date with time
 */
export const formatDateTime = (date, includeSeconds = false) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' })
    };
    
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'Invalid date';
  }
};

/**
 * Format relative date for display
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string for date-fns
 * @returns {string} Formatted date string
 */
export const formatRelativeDate = (date, formatStr = 'MMM d') => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr);
  } catch (error) {
    return '';
  }
};

/**
 * Check if a date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isDateInPast = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateObj < today;
  } catch (error) {
    return false;
  }
};

/**
 * Check if a task is overdue
 * @param {Object} task - Task object with dueDate and status
 * @returns {boolean} True if task is overdue
 */
export const isTaskOverdue = (task) => {
  if (!task?.dueDate || task?.status === 'done') return false;
  
  try {
    const dueDate = typeof task.dueDate === 'string' 
      ? parseISO(task.dueDate) 
      : task.dueDate;
    
    return isAfter(new Date(), endOfDay(dueDate));
  } catch (error) {
    console.error('Error checking if task is overdue:', error);
    return false;
  }
};

/**
 * Check if a milestone is past due
 * @param {Object} milestone - Milestone object with dueDate and status
 * @returns {boolean} True if milestone is past due
 */
export const isMilestonePastDue = (milestone) => {
  if (!milestone?.dueDate) return false;
  return isDateInPast(milestone.dueDate) && milestone.status !== 'completed';
};

/**
 * Get start and end dates for a specific time period
 * @param {string} period - 'day', 'week', 'month', 'quarter', 'year', 'ytd'
 * @returns {Object} Object with startDate and endDate
 */
export const getDateRangeForPeriod = (period) => {
  const today = new Date();
  const startDate = new Date();
  
  switch (period) {
    case 'day':
      // Current day
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'week':
      // Current week (Sunday to today)
      const dayOfWeek = startDate.getDay(); // 0 for Sunday, 6 for Saturday
      startDate.setDate(startDate.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'month':
      // Current month
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'quarter':
      // Current quarter
      const quarter = Math.floor(today.getMonth() / 3);
      startDate.setMonth(quarter * 3);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'year':
      // Current year
      startDate.setMonth(0);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'ytd':
      // Year to date
      startDate.setMonth(0);
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'last30':
      // Last 30 days
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    case 'last90':
      // Last 90 days
      startDate.setDate(startDate.getDate() - 90);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
      
    default:
      // Default to last 30 days
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: today };
  }
};

/**
 * Calculate the difference between two dates in days
 * @param {Date|string} dateA - First date
 * @param {Date|string} dateB - Second date
 * @returns {number} Difference in days
 */
export const getDaysDifference = (dateA, dateB) => {
  const dateObjA = typeof dateA === 'string' ? new Date(dateA) : dateA;
  const dateObjB = typeof dateB === 'string' ? new Date(dateB) : dateB;
  
  // Set time to beginning of day for accurate day calculation
  dateObjA.setHours(0, 0, 0, 0);
  dateObjB.setHours(0, 0, 0, 0);
  
  // Calculate difference in milliseconds and convert to days
  const diffTime = Math.abs(dateObjB - dateObjA);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};